import { useState } from 'react';
import type { Job } from '../../types/job';
import {
  JOB_TYPE_LABELS,
  EXPERIENCE_LEVEL_LABELS,
  LOCATION_TYPE_LABELS,
} from '../../types/job';

interface JobDetailProps {
  job: Job;
  onApply?: () => void;
  onSave?: () => void;
  isSaved?: boolean;
  isApplied?: boolean;
  isOwnJob?: boolean;
  isLoggedIn?: boolean;
}

export function JobDetail({
  job,
  onApply,
  onSave,
  isSaved,
  isApplied,
  isOwnJob,
  isLoggedIn,
}: JobDetailProps) {
  const [showApplyNote, setShowApplyNote] = useState(false);

  const formatSalary = () => {
    if (!job.show_salary || (!job.salary_min && !job.salary_max)) {
      return 'Competitive salary';
    }

    const currency = job.salary_currency || 'EUR';
    const formatter = new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    });

    let salary = '';
    if (job.salary_min && job.salary_max) {
      salary = `${formatter.format(job.salary_min)} - ${formatter.format(job.salary_max)}`;
    } else if (job.salary_min) {
      salary = `From ${formatter.format(job.salary_min)}`;
    } else if (job.salary_max) {
      salary = `Up to ${formatter.format(job.salary_max)}`;
    }

    if (job.salary_period) {
      salary += ` per ${job.salary_period}`;
    }

    return salary;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            {job.company_logo ? (
              <img
                src={job.company_logo}
                alt={job.company_name}
                className="w-16 h-16 rounded-lg object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 font-bold text-2xl">
                {job.company_name?.charAt(0) || 'C'}
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{job.title}</h1>
              <p className="text-lg text-gray-600">{job.company_name}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            {onSave && (
              <button
                onClick={onSave}
                className={`px-4 py-2 rounded-md border font-medium flex items-center gap-2 ${
                  isSaved
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <svg
                  className="w-5 h-5"
                  fill={isSaved ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                  />
                </svg>
                {isSaved ? 'Saved' : 'Save'}
              </button>
            )}
            {onApply && !isOwnJob && (
              <button
                onClick={() => {
                  if (!isLoggedIn) {
                    setShowApplyNote(true);
                    return;
                  }
                  onApply();
                }}
                disabled={isApplied}
                className={`px-6 py-2 rounded-md font-medium ${
                  isApplied
                    ? 'bg-green-100 text-green-800 cursor-default'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isApplied ? 'Applied' : 'Apply Now'}
              </button>
            )}
          </div>
        </div>

        {showApplyNote && !isLoggedIn && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
            Please{' '}
            <a href="/login" className="font-medium underline">
              sign in
            </a>{' '}
            to apply for this job.
          </div>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mt-4">
          {job.is_featured && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              Featured
            </span>
          )}
          {job.is_urgent && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
              Urgent
            </span>
          )}
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            {JOB_TYPE_LABELS[job.job_type]}
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            {EXPERIENCE_LEVEL_LABELS[job.experience_level]}
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            {LOCATION_TYPE_LABELS[job.location_type]}
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
            {job.category}
          </span>
        </div>
      </div>

      {/* Key info grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-gray-50 border-b border-gray-200">
        <div>
          <dt className="text-sm font-medium text-gray-500">Salary</dt>
          <dd className="mt-1 text-sm text-gray-900">{formatSalary()}</dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500">Location</dt>
          <dd className="mt-1 text-sm text-gray-900">
            {job.location_city || LOCATION_TYPE_LABELS[job.location_type]}
            {job.location_country && `, ${job.location_country}`}
          </dd>
        </div>
        {job.timezone && (
          <div>
            <dt className="text-sm font-medium text-gray-500">Timezone</dt>
            <dd className="mt-1 text-sm text-gray-900">{job.timezone}</dd>
          </div>
        )}
        {job.application_deadline && (
          <div>
            <dt className="text-sm font-medium text-gray-500">Apply by</dt>
            <dd className="mt-1 text-sm text-gray-900">{formatDate(job.application_deadline)}</dd>
          </div>
        )}
      </div>

      {/* Contract details (if applicable) */}
      {job.job_type === 'contract' &&
        (job.contract_duration_months || job.contract_rate_min || job.contract_rate_max) && (
          <div className="p-6 bg-blue-50 border-b border-blue-100">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Contract Details</h3>
            <div className="flex gap-6 text-sm text-blue-700">
              {job.contract_duration_months && (
                <span>Duration: {job.contract_duration_months} months</span>
              )}
              {(job.contract_rate_min || job.contract_rate_max) && (
                <span>
                  Rate: {job.contract_rate_min && `€${job.contract_rate_min}`}
                  {job.contract_rate_min && job.contract_rate_max && ' - '}
                  {job.contract_rate_max && `€${job.contract_rate_max}`}
                  /day
                </span>
              )}
            </div>
          </div>
        )}

      {/* Main content */}
      <div className="p-6 space-y-8">
        {/* Description */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">About the Role</h2>
          <div
            className="prose prose-sm max-w-none text-gray-700"
            dangerouslySetInnerHTML={{ __html: job.description }}
          />
        </section>

        {/* Responsibilities */}
        {job.responsibilities && (
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Responsibilities</h2>
            <div
              className="prose prose-sm max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: job.responsibilities }}
            />
          </section>
        )}

        {/* Requirements */}
        {job.requirements && (
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Requirements</h2>
            <div
              className="prose prose-sm max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: job.requirements }}
            />
          </section>
        )}

        {/* Skills */}
        {job.skills && job.skills.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Required Skills</h2>
            <div className="space-y-2">
              {job.skills.map(skill => (
                <div
                  key={skill.id}
                  className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-md"
                >
                  <span className="font-medium text-gray-900">{skill.skill_name}</span>
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    {skill.is_required && (
                      <span className="text-red-600 font-medium">Required</span>
                    )}
                    {skill.min_level && <span>Level: {skill.min_level}</span>}
                    {skill.years_experience && <span>{skill.years_experience}+ years</span>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Benefits */}
        {job.benefits && (
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Benefits</h2>
            <div
              className="prose prose-sm max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: job.benefits }}
            />
          </section>
        )}

        {/* Verification requirements */}
        {job.require_skill_verification && (
          <section className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <h3 className="text-sm font-medium text-yellow-800 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              Skill Verification Required
            </h3>
            <p className="mt-1 text-sm text-yellow-700">
              This position requires verified skills through our assessment platform.
              {job.min_skill_level && ` Minimum level: ${job.min_skill_level}.`}
            </p>
          </section>
        )}
      </div>

      {/* Footer */}
      <div className="p-6 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div>
            <span>Posted: {formatDate(job.published_at || job.created_at)}</span>
            <span className="mx-2">·</span>
            <span>{job.views_count} views</span>
            <span className="mx-2">·</span>
            <span>{job.applications_count} applications</span>
          </div>
          {onApply && !isOwnJob && !isApplied && (
            <button
              onClick={() => {
                if (!isLoggedIn) {
                  setShowApplyNote(true);
                  return;
                }
                onApply();
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700"
            >
              Apply for this Job
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
